import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Shield } from "lucide-react";

export default function Users() {
  const { hasPerm } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [stores, setStores] = useState([]);
  const [editing, setEditing] = useState(null);
  const canManage = hasPerm("users.manage");

  const load = async () => {
    const [u, r, c, s] = await Promise.all([
      api.get("/users"), api.get("/roles"),
      api.get("/users/permissions-catalog"), api.get("/stores"),
    ]);
    setUsers(u.data); setRoles(r.data); setCatalog(c.data); setStores(s.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Usuários</h1>
          <p className="text-zinc-400 mt-1">Gestão dinâmica de acessos e overrides por usuário.</p>
        </div>
        {canManage && (
          <Button data-testid="new-user-btn" onClick={() => setEditing({ store_ids: stores[0] ? [stores[0].id] : [], permission_overrides: { grant: [], deny: [] } })}
            className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
            <UserPlus size={16} className="mr-2" />Novo Usuário
          </Button>
        )}
      </div>

      <div className="grid gap-3">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.email}`} className="alpha-card p-5 flex items-center gap-4">
            <div className="h-11 w-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold-bright font-semibold">
              {u.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">{u.name}</div>
              <div className="text-sm text-zinc-500 truncate">{u.email}</div>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-white/5 text-zinc-300 capitalize flex items-center gap-1">
              <Shield size={12} className="text-gold" />{u.role}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
              {u.active ? "Ativo" : "Inativo"}
            </span>
            <span className="hidden md:block text-xs text-zinc-500 w-32 text-right" data-testid={`user-lastlogin-${u.email}`}>
              {u.last_login ? new Date(u.last_login).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "Nunca acessou"}
            </span>
            {canManage && (
              <Button data-testid={`edit-user-${u.email}`} variant="ghost" size="sm" className="text-gold-bright"
                onClick={() => setEditing({ ...u })}>Editar</Button>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <UserDialog user={editing} roles={roles} catalog={catalog} stores={stores}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function UserDialog({ user, roles, catalog, stores, onClose, onSaved }) {
  const isNew = !user.id;
  const [form, setForm] = useState({
    name: user.name || "", email: user.email || "", password: "",
    role: user.role || (roles[0]?.slug || ""), active: user.active ?? true,
    store_ids: user.store_ids || [],
    permission_overrides: user.permission_overrides || { grant: [], deny: [] },
  });
  const [saving, setSaving] = useState(false);

  const toggleOverride = (key, kind) => {
    setForm((f) => {
      const ov = { grant: [...(f.permission_overrides.grant || [])], deny: [...(f.permission_overrides.deny || [])] };
      const other = kind === "grant" ? "deny" : "grant";
      if (ov[kind].includes(key)) ov[kind] = ov[kind].filter((k) => k !== key);
      else { ov[kind].push(key); ov[other] = ov[other].filter((k) => k !== key); }
      return { ...f, permission_overrides: ov };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/users", form);
      else {
        const payload = { name: form.name, role: form.role, active: form.active,
          store_ids: form.store_ids, permission_overrides: form.permission_overrides };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${user.id}`, payload);
      }
      toast.success("Usuário salvo");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Novo Usuário" : "Editar Usuário"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label className="text-xs text-zinc-400">Nome</Label>
            <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div><Label className="text-xs text-zinc-400">E-mail</Label>
            <Input data-testid="user-email" type="email" disabled={!isNew} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1 disabled:opacity-50" /></div>
          <div><Label className="text-xs text-zinc-400">{isNew ? "Senha" : "Nova senha (opcional)"}</Label>
            <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
          <div><Label className="text-xs text-zinc-400">Cargo</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121217] border-gold/20 text-white">
                {roles.map((r) => <SelectItem key={r.slug} value={r.slug} className="focus:bg-gold/10">{r.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          {!isNew && (
            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active" />
              <Label className="text-sm text-zinc-300">Usuário ativo</Label>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-400 mb-2 block">Overrides de permissão (por usuário)</Label>
            <div className="space-y-1.5 border border-white/10 rounded-xl p-3 max-h-52 overflow-y-auto">
              {catalog.map((p) => {
                const granted = form.permission_overrides.grant?.includes(p.key);
                const denied = form.permission_overrides.deny?.includes(p.key);
                return (
                  <div key={p.key} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">{p.label} <span className="text-zinc-600 text-xs">{p.module}</span></span>
                    <div className="flex gap-1">
                      <button onClick={() => toggleOverride(p.key, "grant")} className={`px-2 py-0.5 rounded text-xs ${granted ? "bg-green-500/20 text-green-400" : "bg-white/5 text-zinc-500"}`}>Permitir</button>
                      <button onClick={() => toggleOverride(p.key, "deny")} className={`px-2 py-0.5 rounded text-xs ${denied ? "bg-red-500/20 text-red-400" : "bg-white/5 text-zinc-500"}`}>Negar</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="save-user-btn" onClick={save} disabled={saving} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
